/**
 * The arithmetic behind the Hours pane: the business's wall clock, whether a location is open
 * on it, and the checks a location's week has to pass before Done.
 *
 * THE WORKER HAS THE SAME RULES AND THEY ARE THE ONES THAT COUNT (`domain/hours.ts`). These
 * are here so the dialog can say "Tuesday closes before it opens" beside Tuesday, while the
 * owner is looking at it, instead of as a failed save a screen later. The messages match, so
 * the one that slips through reads the same either way.
 */

import { DAY_KEYS, type Closure, type DayKey, type Location, type Period, type Week } from "../../api";

/** Monday first, which is how a week of opening hours is read. */
export const WEEK: ReadonlyArray<readonly [DayKey, string]> = [
  ["mon", "Monday"],
  ["tue", "Tuesday"],
  ["wed", "Wednesday"],
  ["thu", "Thursday"],
  ["fri", "Friday"],
  ["sat", "Saturday"],
  ["sun", "Sunday"],
];

export const MAX_LOCATIONS = 10;
export const MAX_PERIODS = 3;

export function closedWeek(): Week {
  return { sun: [], mon: [], tue: [], wed: [], thu: [], fri: [], sat: [] };
}

/** What a new location opens as: the week most businesses start from, to be corrected. */
export function officeWeek(): Week {
  const nine: Period[] = [["09:00", "17:00"]];
  return { ...closedWeek(), mon: nine, tue: nine, wed: nine, thu: nine, fri: nine };
}

/** `loc_1f3a…`, the shape the Worker's own ids have, so a saved one looks like any other. */
export function newId(prefix: "loc" | "cls"): string {
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  return `${prefix}_${[...bytes].map((b) => b.toString(16).padStart(2, "0")).join("")}`;
}

export function toMinutes(time: string): number {
  return Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));
}

export function fromMinutes(minutes: number): string {
  const clamped = Math.max(0, Math.min(24 * 60, minutes));
  return `${String(Math.floor(clamped / 60)).padStart(2, "0")}:${String(clamped % 60).padStart(2, "0")}`;
}

/** Every quarter hour from 00:00 to 24:00, the choices a time select offers. */
export const QUARTERS: string[] = Array.from({ length: 97 }, (_, i) => fromMinutes(i * 15));

/**
 * What is wrong with one day's periods, in the Worker's words, or null.
 * `day` is the day's name as the sentence starts with it.
 */
export function dayProblem(day: string, periods: Period[]): string | null {
  if (periods.some(([open, close]) => toMinutes(open) >= toMinutes(close))) return `${day} closes before it opens`;
  const sorted = [...periods].sort((a, b) => toMinutes(a[0]) - toMinutes(b[0]));
  for (let i = 1; i < sorted.length; i++) {
    if (toMinutes(sorted[i]![0]) < toMinutes(sorted[i - 1]![1])) return `${day} has opening times that overlap`;
  }
  return null;
}

/** The next period after the last one: an hour after it closes, for two hours. */
export function nextPeriod(periods: Period[]): Period {
  const last = periods[periods.length - 1];
  const start = last ? toMinutes(last[1]) + 60 : 9 * 60;
  return [fromMinutes(start), fromMinutes(start + 120)];
}

/**
 * Now, as a clock on the wall of the business: weekday, minutes past midnight and the date.
 * The browser's own zone when the agent has none, which is at least somebody's wall.
 */
export interface WallClock {
  day: DayKey;
  minute: number;
  /** `YYYY-MM-DD` */
  date: string;
}

export function wallClock(now: number, timeZone?: string | null): WallClock {
  const read = (zone: string | undefined) =>
    new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      weekday: "short",
      hour: "numeric",
      minute: "2-digit",
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date(now));
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = read(timeZone || undefined);
  } catch {
    parts = read(undefined);
  }
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
  const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(part("weekday"));
  return {
    day: DAY_KEYS[Math.max(0, dow)]!,
    minute: (Number(part("hour")) % 24) * 60 + Number(part("minute")),
    date: `${part("year")}-${part("month")}-${part("day")}`,
  };
}

export function closureOn(location: Location, date: string): Closure | undefined {
  return location.closures.find((c) => c.from <= date && date <= c.to);
}

export function openAt(location: Location, clock: WallClock): boolean {
  if (closureOn(location, clock.date)) return false;
  return location.hours[clock.day].some(([open, close]) => clock.minute >= toMinutes(open) && clock.minute < toMinutes(close));
}

/** Closures that have not ended by `today`, soonest first. Ended ones are dropped on save. */
export function upcoming(closures: Closure[], today: string): Closure[] {
  return closures.filter((c) => c.to >= today).sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function parts(date: string) {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return { y, m, d, dow: new Date(Date.UTC(y, m - 1, d)).getUTCDay() };
}

/**
 * `24 Dec`, `24–26 Dec`, `30 Dec – 2 Jan`, with the year only when it is not this one.
 * `weekday` puts the day name in front of each end, for the list in the dialog.
 */
export function closureRange(closure: Pick<Closure, "from" | "to">, today: string, weekday = false): string {
  const a = parts(closure.from);
  const b = parts(closure.to);
  const thisYear = parts(today).y;
  const one = (p: ReturnType<typeof parts>, year: boolean) =>
    `${weekday ? `${WEEKDAYS[p.dow]} ` : ""}${p.d} ${MONTHS[p.m - 1]}${year ? ` ${p.y}` : ""}`;
  if (closure.from === closure.to) return one(a, a.y !== thisYear);
  if (!weekday && a.y === b.y && a.m === b.m) return `${a.d}–${b.d} ${MONTHS[a.m - 1]}${a.y !== thisYear ? ` ${a.y}` : ""}`;
  return `${one(a, a.y !== b.y || a.y !== thisYear)} – ${one(b, b.y !== thisYear)}`;
}

/** The grey words at the right of the Closures row. */
export function closuresSummary(closures: Closure[], today: string): string {
  const next = upcoming(closures, today);
  if (!next.length) return "None scheduled";
  if (next.length === 1) return closureRange(next[0]!, today);
  return `${next.length} upcoming`;
}
